import { toast } from 'sonner'
import { prepacksApi } from '@/services/api'

// A schedule or template save reaches the packs it made before (#1089): the
// server queues an update and names it on the save's response
// (`propagation_id`, or a template update's). One toast follows a save's
// updates: "Updating linked packs…", then what they did, with View (the
// packs in the library) and Retry after a failure. It reads each update
// every POLL_MS until it is done or failed, at most MAX_POLLS times, and
// outlives the page that saved, as the toast does.

const POLL_MS = 1500
const MAX_POLLS = 120
const RESULT_DURATION_MS = 10000

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms) })

const finished = (update) => update?.status === 'done' || update?.status === 'failed'

const count = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** The distinct update ids saves answered with, in order. */
export function savedUpdateIds(...responses) {
  const ids = []
  for (const response of responses) {
    const id = response?.propagation_id
    if (id && !ids.includes(id)) ids.push(id)
  }
  return ids
}

/** What the updates did, as one line: "12 packs updated · 3 off surfaces". */
export function packUpdateSummary(updates) {
  const totals = { updated: 0, published: 0, removed: 0, repainting: 0, attention: 0, failed: 0 }
  for (const update of updates) {
    for (const key of Object.keys(totals)) totals[key] += Number(update?.counts?.[key]) || 0
    if (update?.status === 'failed' && !(update?.counts?.failed > 0)) totals.failed += 1
  }
  const parts = []
  if (totals.updated) parts.push(`${count(totals.updated, 'pack')} updated`)
  if (totals.published) parts.push(`${totals.published} published`)
  if (totals.removed) parts.push(`${totals.removed} off surfaces`)
  if (totals.repainting) parts.push(`${count(totals.repainting, 'picture')} repainting`)
  if (totals.attention) parts.push(`${totals.attention} ${totals.attention === 1 ? 'needs' : 'need'} attention`)
  if (totals.failed) parts.push(`${totals.failed} couldn’t update`)
  return parts.length ? parts.join(' · ') : 'Linked packs are up to date'
}

/** Why the first named pack did not simply update, and how many more were named. */
export function packUpdateDetail(updates) {
  const items = updates.flatMap((update) => update?.items || [])
  const failure = updates.find((update) => update?.status === 'failed' && update?.error)
  if (items.length === 0) return failure ? failure.error : undefined
  const [first] = items
  const more = items.length > 1 ? ` (and ${items.length - 1} more)` : ''
  return `“${first.title}”: ${first.reason}${more}`
}

/**
 * Follows the updates `ids` of a save in one toast. `saved` opens its
 * messages ("Changes saved"), null for none; `onView` opens the packs.
 * `getUpdate` and `retryUpdate` default to the API, `pollMs` to POLL_MS.
 */
export async function followPackUpdates({ slug, ids, saved = null, queryClient, onView, getUpdate = prepacksApi.getPropagation, retryUpdate = prepacksApi.retryPropagation, pollMs = POLL_MS }) {
  const lead = (text) => (saved ? `${saved}. ${text}` : text)
  if (!ids?.length) {
    if (saved) toast.success(saved)
    return
  }
  const toastId = toast.loading(lead('Updating linked packs…'))
  let updates = []
  for (let poll = 0; poll < MAX_POLLS; poll += 1) {
    try {
      updates = await Promise.all(ids.map((id) => getUpdate(slug, id)))
    } catch {
      // A missed read leaves the last answer; the next read tries again.
    }
    if (updates.length === ids.length && updates.every(finished)) break
    await sleep(pollMs)
  }
  if (updates.length !== ids.length || !updates.every(finished)) {
    toast.message(lead('Linked packs are still updating.'), { id: toastId, duration: RESULT_DURATION_MS })
    return
  }
  queryClient?.invalidateQueries({ queryKey: ['prepacks', 'library', slug] })
  queryClient?.invalidateQueries({ queryKey: ['prepacks', 'surfaces', slug] })
  const summary = packUpdateSummary(updates)
  const description = packUpdateDetail(updates)
  const retryable = updates.filter((update) => update.status === 'failed' || update.counts?.failed > 0)
  if (retryable.length) {
    toast.warning(lead(summary), {
      id: toastId,
      description,
      duration: RESULT_DURATION_MS,
      action: {
        label: 'Retry',
        onClick: async () => {
          let queued
          try {
            queued = await Promise.all(retryable.map((update) => retryUpdate(slug, update.id)))
          } catch {
            toast.error('Couldn’t retry. Try again in a moment.')
            return
          }
          // A retry with no pack left to update answers with the finished
          // update; two retries can join one waiting update.
          const ids = [...new Set(queued.filter((update) => !finished(update)).map((update) => update.id))]
          if (!ids.length) {
            toast.success('Linked packs are up to date', { id: toastId })
            return
          }
          await followPackUpdates({ slug, ids, queryClient, onView, getUpdate, retryUpdate, pollMs })
        },
      },
    })
    return
  }
  toast.success(lead(summary), {
    id: toastId,
    description,
    duration: RESULT_DURATION_MS,
    ...(onView ? { action: { label: 'View', onClick: onView } } : {}),
  })
}

/** The library opened on a schedule's packs, or a template's. */
export const libraryPathFor = ({ schedule, template }) => {
  const params = new URLSearchParams()
  if (schedule) params.set('schedule', schedule)
  if (template) params.set('template', template)
  return `/dashboard/prepacks/library?${params.toString()}`
}
